// Heading / Normal from the toolbar must change only the selected line(s) (tasks/bug-fixes.md):
// - a selection that ends at the very start of the next block (e.g. a whole line selected by dragging
//   or triple-click) must not pull that next block in;
// - "lines" separated by hard breaks (Shift+Enter, common in pasted text) live in one paragraph, and a
//   block type always applies to the whole paragraph — so the paragraph is first split at the hard
//   breaks around the selection, making the selected line(s) a block of their own.
import { Selection, TextSelection, Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';

/** End of the selection, pulled back into the last selected block when it sits at the start of the next. */
function effectiveSelectionEnd(tr: Transaction): number {
  const { from, to, $to } = tr.selection;
  if (to <= from || $to.parentOffset !== 0 || $to.depth === 0) {
    return to;
  }
  const before = Selection.findFrom(tr.doc.resolve($to.before()), -1, true);
  return before && before.to >= from ? before.to : to;
}

/** Positions (absolute) of the hard breaks directly inside `block`, which starts at `blockStart`. */
function hardBreakPositions(block: PMNode, blockStart: number): number[] {
  const positions: number[] = [];
  block.forEach((child, offset) => {
    if (child.type.name === 'hardBreak') {
      positions.push(blockStart + 1 + offset);
    }
  });
  return positions;
}

/**
 * Prepares `tr` so a block-type change applies to just the selected line(s): splits the enclosing
 * paragraph at the hard break before the selection and the one after it, and leaves a text selection
 * covering exactly those line(s). Returns true if it changed anything.
 */
export function isolateSelectedLines(tr: Transaction): boolean {
  const startSelection = tr.selection;
  let from = startSelection.from;
  let to = effectiveSelectionEnd(tr);
  const { $from } = startSelection;
  const $to = tr.doc.resolve(to);
  if (!$from.parent.isTextblock || !$to.parent.isTextblock) {
    if (to !== startSelection.to) {
      tr.setSelection(TextSelection.create(tr.doc, from, to));
      return true;
    }
    return false;
  }

  // Split after the selection first (higher position), so `from` stays valid.
  const endBlockStart = $to.before();
  const breakAfter = hardBreakPositions($to.parent, endBlockStart).find((pos) => pos >= to);
  if (breakAfter !== undefined) {
    tr.delete(breakAfter, breakAfter + 1);
    tr.split(breakAfter);
  }

  const startBlockStart = $from.before();
  const breaksBefore = hardBreakPositions(tr.doc.resolve(from).parent, startBlockStart).filter((pos) => pos < from);
  const breakBefore = breaksBefore.length ? breaksBefore[breaksBefore.length - 1] : undefined;
  if (breakBefore !== undefined) {
    tr.delete(breakBefore, breakBefore + 1);
    tr.split(breakBefore);
    // `from` was after the deleted break (−1) and is now after the new block boundary (+2).
    from = from + 1;
    to = to + 1;
  }

  const changed = breakAfter !== undefined || breakBefore !== undefined || to !== startSelection.to;
  if (changed) {
    const size = tr.doc.content.size;
    tr.setSelection(TextSelection.create(tr.doc, Math.min(from, size), Math.min(Math.max(to, from), size)));
  }
  return changed;
}
